import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material"

import { customersApi } from "@crm-src/lib/customers-api"
import usePermissions from "@crm-src/hooks/usePermissions"

import { fetchInternalCompany } from "./licences-api"

/**
 * New licence — pick WHOSE appliance, then build it.
 *
 * Nothing is created here. The dialog only answers the one question the
 * builder cannot (which customer) and lands in `/crm/licences/new`, where the
 * terms are chosen and Generate creates AND issues in one press. There is no
 * half-generated licence to leave behind if the operator changes their mind.
 *
 * OUR OWN APPLIANCES ARE PINNED, NOT SEARCHABLE. XOP Networks Inc. is hidden
 * from every customer listing by crm-api (`Customer.external`), so typing its
 * name finds nothing. It is offered as a fixed first option to a super admin —
 * and to nobody else, because a super admin is the only actor crm-api will let
 * write one. See docs/superpowers/specs/2026-09-13-internal-licences-design.md.
 */

export interface CustomerOption {
  id: number
  name: string
  /** One of XOP's own boxes. Renders as a pinned entry with a chip; the picker
   * never returns it from a search. */
  internal?: boolean
}

/** Where a new licence for this customer is built.
 *
 * `internal=1` is PRESENTATION ONLY — it lets the builder label an unsaved
 * licence as one of our own boxes before a row exists to read it from. Forging
 * it changes a chip and nothing else: crm-api decides who may write an internal
 * licence from the customer id, never from this. */
export function newLicencePath(customer: CustomerOption): string {
  const base = `/crm/licences/new?customer=${customer.id}&name=${encodeURIComponent(customer.name)}`
  return customer.internal ? `${base}&internal=1` : base
}

export const TRIAL_DAYS = 30

export function defaultTrialEnd(today = new Date()): string {
  const date = new Date(today)
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() + TRIAL_DAYS)
  const month = `${date.getMonth() + 1}`.padStart(2, "0")
  const day = `${date.getDate()}`.padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

export function CreateLicenceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { canManageInternalLicences } = usePermissions()
  const mayLicenceOurOwn = canManageInternalLicences()
  const [picked, setPicked] = useState<CustomerOption | null>(null)
  const [options, setOptions] = useState<CustomerOption[]>([])
  const [internalCompany, setInternalCompany] = useState<CustomerOption | null>(null)
  const [query, setQuery] = useState("")
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (open) setPicked(null)
  }, [open])

  // Our own company, asked for once per open. A refusal or a fresh box with no
  // internal company yet simply means there is no entry to pin — never an error
  // on screen, because the operator did not ask for it.
  useEffect(() => {
    if (!open || !mayLicenceOurOwn) return
    let cancelled = false
    void (async () => {
      try {
        const company = await fetchInternalCompany()
        if (!cancelled && company) setInternalCompany({ ...company, internal: true })
      } catch {
        if (!cancelled) setInternalCompany(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, mayLicenceOurOwn])

  // Debounced company search. Customers only — a prospect has no appliance.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const handle = setTimeout(async () => {
      setSearching(true)
      try {
        const response = await customersApi.getAll({ search: query, customer_type: "Customer", per_page: 20 })
        if (cancelled) return
        setOptions(response.customers.map((row) => ({ id: Number(row.id), name: row.company_name || row.full_name })))
      } catch {
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [open, query])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New licence</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          One licence per appliance. Pick the {mayLicenceOurOwn ? "customer, or one of our own boxes" : "customer"}; you
          choose the term, ports and features next and generate the file in one step. A {TRIAL_DAYS}-day
          trial issues at once for a customer with no licence yet; a full licence waits for a super
          admin&apos;s approval — our own appliances included.
        </DialogContentText>
        <Autocomplete
          options={internalCompany ? [internalCompany, ...options] : options}
          value={picked}
          loading={searching}
          onChange={(_event, next) => {
            setPicked(next)
            // Search everyone again, so reopening the list offers a DIFFERENT
            // customer rather than only the one just picked.
            setQuery("")
          }}
          onInputChange={(_event, next, reason) => {
            // "reset" is MUI writing the picked name into the box. Treating it
            // as a search narrowed the list to that one company, and a second
            // customer could not be picked (2026-09-15).
            if (reason === "reset") return
            setQuery(next)
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(all) => all}
          groupBy={(option) => (option.internal ? "Our own appliances" : "Customers")}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ display: "flex", gap: 1 }}>
              {option.name}
              {option.internal ? <Chip size="small" label="Internal" color="primary" variant="outlined" /> : null}
            </Box>
          )}
          noOptionsText={query ? "No customer matches." : "Type a company name."}
          renderInput={(params) => (
            <TextField
              {...params}
              label={mayLicenceOurOwn ? "Customer or our own appliance" : "Customer"}
              size="small"
              autoFocus
              placeholder="Search companies"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!picked}
          onClick={() => {
            if (!picked) return
            onClose()
            navigate(newLicencePath(picked))
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
